import type { CastRow, LocationRow, PropRow } from "@/components/prompt-editor/mention-sources"
import { librarySourceKey, projectAssetSource, projectUploadSource } from "@/lib/library-source"
import type { CanvasReferenceItem } from "@/types/canvas"

function propStateLabel(name: string, stateName?: string | null): string {
  const state = stateName?.trim()
  return state ? `${name} (${state})` : name
}

const LOCATION_PRIMARY_FACING = "three_quarter" as const

function locationCandidateDisplayUrl(l: LocationRow): string {
  return l.reference_urls_by_facing?.[LOCATION_PRIMARY_FACING]?.trim() || l.reference_sheet_url?.trim() || ""
}

function locationCandidateThumbUrl(l: LocationRow): string {
  return l.reference_thumb_urls_by_facing?.[LOCATION_PRIMARY_FACING]?.trim() || ""
}

function locationCandidateDownloadUrl(l: LocationRow): string {
  return (
    l.reference_download_urls_by_facing?.[LOCATION_PRIMARY_FACING]?.trim() ||
    l.reference_sheet_download_url?.trim() ||
    ""
  )
}

/** Full same-origin proxy for sketch — never display/thumb/`fetch_url`. */
export function sketchSourceUrlFromCanvasRef(ref: CanvasReferenceItem): string | null {
  return ref.download_url?.trim() || null
}

/** Caption for an attached entity input: picker key / same blob as a candidate. */
export function labelForSignedReferenceInput(
  item: { blob_id?: number | null; source_reference_key?: string | null },
  characters: CastRow[],
  locations: LocationRow[],
  props: PropRow[],
): string | null {
  const candidates = [
    ...buildCharacterReferenceCandidates(characters),
    ...buildLocationReferenceCandidates(locations),
    ...buildPropReferenceCandidates(props),
  ]
  const key = item.source_reference_key?.trim()
  if (key) {
    const hit = candidates.find((ref) => canvasReferenceKey(ref) === key)
    const label = hit?.label?.trim()
    if (label) return label
  }
  if (item.blob_id != null) {
    const hit = candidates.find((ref) => ref.blob_id === item.blob_id)
    const label = hit?.label?.trim()
    if (label) return label
  }
  return null
}

export function canvasReferenceKey(ref: CanvasReferenceItem): string {
  if (ref.entity_type && ref.entity_id != null) {
    const statePart = ref.state_id != null ? `:${ref.state_id}` : ""
    return `${ref.entity_type}:${ref.entity_id}${statePart}`
  }
  return ref.url
}

/** Entity placement (3D scene): match without state_id so all prop-state cards share one key. */
export function entityPickKey(ref: CanvasReferenceItem): string {
  if (ref.entity_type && ref.entity_id != null) {
    return `${ref.entity_type}:${ref.entity_id}`
  }
  return canvasReferenceKey(ref)
}

export function referenceHasPickerPreview(ref: CanvasReferenceItem): boolean {
  return Boolean(ref.url?.trim())
}

export function referencePickerCandidateUnavailable(ref: CanvasReferenceItem, existingKeys: string[]): boolean {
  if (existingKeys.includes(canvasReferenceKey(ref))) return true
  if (ref.entity_type === "character") {
    return ref.attachable === false
  }
  return !referenceHasPickerPreview(ref)
}

export function buildExistingReferenceKeys(
  items: Array<{ url: string; blob_id?: number | null; source_reference_key?: string | null }>,
  characters: CastRow[],
  locations: LocationRow[],
  props: PropRow[],
): string[] {
  const urls = new Set(items.map((item) => item.url).filter(Boolean))
  const keys = new Set<string>(items.map((item) => item.url).filter(Boolean))

  const attachedBlobIds = new Set<number>()
  for (const item of items) {
    if (item.source_reference_key) keys.add(item.source_reference_key)
    // Ассет Библиотеки прикреплён ссылкой, ключа источника у него нет: у загрузки
    // проекта entity_id — это id блоба, поэтому ключ восстанавливается по нему.
    // project_asset — лист героя/локации/реквизита, прикреплённый тем же blob_id.
    if (item.blob_id != null) {
      attachedBlobIds.add(item.blob_id)
      keys.add(librarySourceKey(projectUploadSource(item.blob_id)))
      keys.add(librarySourceKey(projectAssetSource(item.blob_id)))
    }
  }

  const allCandidates = [
    ...buildCharacterReferenceCandidates(characters),
    ...buildLocationReferenceCandidates(locations),
    ...buildPropReferenceCandidates(props),
  ]

  for (const ref of allCandidates) {
    const urlHit =
      urls.has(ref.url) || (ref.fetch_url && urls.has(ref.fetch_url)) || (ref.thumb_url && urls.has(ref.thumb_url))
    const blobHit = ref.blob_id != null && attachedBlobIds.has(ref.blob_id)
    if (urlHit || blobHit) {
      keys.add(canvasReferenceKey(ref))
    }
  }

  return [...keys]
}

export function buildCharacterReferenceCandidates(characters: CastRow[]): CanvasReferenceItem[] {
  return characters.map((c) => {
    // Tile/lightbox match attach: sheet/ref first; portrait only as fallback.
    const portraitThumb = c.portrait_thumb_url?.trim() || ""
    const portraitDisplay = c.portrait_url?.trim() || ""
    const sheetThumb = c.character_sheet_url?.trim() || ""
    const sheetDisplay = c.character_sheet_display_url?.trim() || ""
    const refThumb = c.reference_sheet_url?.trim() || ""
    const refDisplay = c.reference_display_url?.trim() || ""
    const fetch_url = sheetDisplay || refDisplay || sheetThumb || refThumb || portraitDisplay || portraitThumb
    const url = fetch_url
    const download_url =
      c.character_sheet_download_url?.trim() ||
      c.reference_download_url?.trim() ||
      c.portrait_download_url?.trim() ||
      null

    return {
      kind: "entity",
      label: c.name,
      url,
      fetch_url,
      download_url,
      thumb_url: sheetThumb || refThumb || portraitThumb || null,
      blob_id: c.attach_blob_id ?? null,
      media_type: "image" as const,
      entity_type: "character",
      entity_id: c.id,
      attachable: Boolean(fetch_url),
    }
  })
}

export function buildLocationReferenceCandidates(locations: LocationRow[]): CanvasReferenceItem[] {
  return locations.map((l) => {
    const thumb = locationCandidateThumbUrl(l)
    const display = locationCandidateDisplayUrl(l)
    const download = locationCandidateDownloadUrl(l)
    const url = display || thumb
    return {
      kind: "entity",
      label: l.name,
      url,
      fetch_url: display || thumb || undefined,
      download_url: download || null,
      thumb_url: thumb || null,
      blob_id: l.attach_blob_id ?? null,
      media_type: "image" as const,
      entity_type: "location",
      entity_id: l.id,
    }
  })
}

export function buildPropReferenceCandidates(props: PropRow[]): CanvasReferenceItem[] {
  const out: CanvasReferenceItem[] = []
  for (const p of props) {
    const thumb = p.front_view_thumb_url?.trim() || p.thumb_url?.trim() || p.reference_sheet_url?.trim() || ""
    const display =
      p.front_view_url?.trim() ||
      p.front_view_download_url?.trim() ||
      p.reference_display_url?.trim() ||
      p.reference_sheet_url?.trim() ||
      ""
    const baseUrl = display || thumb
    const baseThumb = thumb || null
    const baseDownload = p.front_view_download_url?.trim() || p.reference_download_url?.trim() || null
    out.push({
      kind: "entity",
      label: p.name,
      url: baseUrl,
      fetch_url: display || thumb || undefined,
      download_url: baseDownload,
      thumb_url: baseThumb,
      blob_id: p.attach_blob_id ?? null,
      media_type: "image",
      entity_type: "prop",
      entity_id: p.id,
      state_id: null,
    })
    for (const state of p.states ?? []) {
      const stateThumb = state.reference_url?.trim() || ""
      const stateDisplay = state.reference_full_url?.trim() || ""
      const stateDownload = state.reference_download_url?.trim() || ""
      const stateUrl = stateDisplay || stateThumb || baseUrl
      out.push({
        kind: "entity",
        label: propStateLabel(p.name, state.name),
        url: stateUrl,
        fetch_url: stateDisplay || stateThumb || baseUrl || undefined,
        download_url: stateDownload || baseDownload,
        thumb_url: stateThumb || baseThumb,
        blob_id: state.attach_blob_id ?? p.attach_blob_id ?? null,
        media_type: "image",
        entity_type: "prop",
        entity_id: p.id,
        state_id: state.id,
      })
    }
  }
  return out
}
